import { Box, InputAdornment, useTheme } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import { ChevronRight } from '@material-ui/icons'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { isEmpty, isEqual } from 'lodash'
import { useFormContext } from 'react-hook-form'
import { ControlledTextFieldProps, KeyLabelType } from '../../../types'
import { MultiSelectFiltersModal } from '../MultiSelectFiltersModal'

const useStyles = makeStyles((theme: Theme) => ({
  chevron: {
    position: 'absolute',
    top: '50%',
    transform: 'translateX(-50%)',
    right: theme.spacing(1),
    rotate: '90deg',
    color: '#999',
  },
}))

const MobileMultipleSelectorComponent: FC<
  {
    name: string
    optionsList: KeyLabelType[]
    readOnly?: boolean
    doubleRows?: boolean
  } & Partial<ControlledTextFieldProps>
> = ({ name, optionsList, doubleRows, readOnly, ...rest }) => {
  const classes = useStyles()
  const theme = useTheme()

  const { errors, setValue, watch } = useFormContext()
  const currentValue = watch(name)
  const error = errors ? errors[name || 'FormAutocompleteSelect'] : null

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tempValuesList, setTempValuesList] = useState<Array<string | number>>([])

  const selectedLabelsString = useMemo(
    () =>
      optionsList
        .filter(({ key }) => currentValue?.includes(key))
        .map(({ label }) => label)
        .join(', '),
    [currentValue, optionsList],
  )

  const resetList = useCallback(() => {
    setTempValuesList([])
    setValue(name, [])
    setIsModalOpen(false)
  }, [name])

  const applyOptions = useCallback(() => {
    setValue(name, tempValuesList, { shouldDirty: true, shouldValidate: true })
    setIsModalOpen(false)
  }, [setValue, tempValuesList])

  const handleSelect = useCallback(
    (optionKey: number | string) => () => {
      const isChecked = tempValuesList.includes(optionKey)

      const newValue = !isChecked
        ? [...tempValuesList, optionKey]
        : tempValuesList.filter((item) => item !== optionKey)

      setTempValuesList(newValue)
    },
    [optionsList, tempValuesList],
  )

  const handleToggle = useCallback(() => setIsModalOpen((prevState) => !prevState), [])

  useEffect(() => {
    if (currentValue && isEmpty(tempValuesList) && !isModalOpen) {
      setTempValuesList(currentValue)
    }
  }, [isModalOpen])

  return (
    <>
      <Box width="100%" onClick={handleToggle}>
        <TextField
          error={!!error}
          helperText={error ? error.message : null}
          {...rest}
          inputProps={{
            ...rest.inputProps,
            readOnly: true,
            tabIndex: -1,
            'data-test': `autotest-${name}`,
            style: { paddingRight: theme.spacing(4) },
          }}
          InputProps={{
            value: selectedLabelsString,
            endAdornment: (
              <InputAdornment position="end">
                <ChevronRight className={classes.chevron} />
              </InputAdornment>
            ),
          }}
          data-test={`autotest-${name}`}
          InputLabelProps={{ shrink: !isEmpty(currentValue) }}
          multiline
          fullWidth
        />
      </Box>

      <MultiSelectFiltersModal
        name={name}
        open={isModalOpen}
        onClose={applyOptions}
        onSelect={handleSelect}
        textHeader={(rest?.label as string) || name}
        options={optionsList}
        currentValue={tempValuesList}
        resetList={resetList}
        doubleRows={doubleRows}
      />
    </>
  )
}

export const MobileMultipleSelector = React.memo(MobileMultipleSelectorComponent, isEqual)
